import { Link } from "react-router-dom";
import {
    ScanLine, ArrowLeftRight, Database, Wand2, Recycle, LayoutTemplate,
    FileSpreadsheet, Tags, ScanText, FileSignature, Truck, ClipboardList,
    FileStack, Layers, Stamp, ShieldCheck, TrendingUp, LifeBuoy,
    Gauge, Coins, ArrowUpRight, Sparkles, Target, Calculator, FolderTree,
    BookOpen, BookMarked, Award, AlertTriangle, Search, Zap,
    Download, Rocket, Brain, Repeat, ShieldAlert, GitBranch, Cloud, Boxes,
    Network, PlayCircle, ClipboardCheck, Sliders,
} from "lucide-react";
import { EDI_MODULES, EDI_SECTIONS, edi_by_section } from "@/lib/edi/modules";
import { EDI } from "@/constants/testIds";

const ICONS = {
    ScanLine, ArrowLeftRight, Database, Wand2, Recycle, LayoutTemplate,
    FileSpreadsheet, Tags, ScanText, FileSignature, Truck, ClipboardList,
    FileStack, Layers, Stamp, ShieldCheck, TrendingUp, LifeBuoy,
    Target, Calculator, FolderTree, BookOpen, BookMarked, Award,
    AlertTriangle, Search, Sparkles, Repeat, ShieldAlert, Rocket,
    GitBranch, Cloud, Boxes, Network, Zap,
};

const PILLARS = [
    { key: "capture",   name: "Capture™",   icon: Download, desc: "Capture enterprise knowledge once — from every project, discipline and legacy archive." },
    { key: "transform", name: "Transform™", icon: Wand2,    desc: "Transform raw information into corporate intelligence — OCR, AI classification, similarity, relationships." },
    { key: "reuse",     name: "Reuse™",     icon: Recycle,  desc: "Reuse drawings, data sheets, calculations, specifications, packages and lessons — instead of recreating them." },
    { key: "deliver",   name: "Deliver™",   icon: Rocket,   desc: "Accelerate engineering, procurement, commercial, contracts and executive reporting with enterprise intelligence." },
    { key: "learn",     name: "Learn™",     icon: Brain,    desc: "Every completed project increases corporate knowledge and makes the next one faster, better, cheaper." },
];

const SECTION_ACCENT = Object.fromEntries(EDI_SECTIONS.map((s) => [s.key, s.accent]));

export default function EnterpriseIntelligenceHub() {
    const grouped = edi_by_section();

    return (
        <div className="space-y-10" data-testid={EDI.hub}>
            {/* Header */}
            <div className="relative overflow-hidden rounded-2xl glass p-8">
                <div
                    className="absolute inset-0 opacity-40 pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(ellipse at 90% 10%, rgba(212,175,55,0.22), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(59,130,246,0.18), transparent 55%)",
                    }}
                />
                <div className="relative">
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter leading-[0.98]">
                        EPCM360 is more than project management.
                    </h1>
                    <p className="mt-4 max-w-3xl text-slate-300 font-body text-base leading-relaxed">
                        Capture organisational knowledge once. Preserve it as a corporate asset.
                        Reuse it across every future engagement. This is the Enterprise Delivery
                        Intelligence Platform™ — how SGS Bateman wins work faster, engineers
                        more efficiently, and protects intellectual capital at scale.
                    </p>

                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link
                            to="/edi/executive-demo"
                            data-testid={EDI.goExecutiveDemo}
                            className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] transition-colors duration-200"
                        >
                            <PlayCircle className="h-4 w-4" strokeWidth={2} />
                            Executive Rehearsal
                        </Link>
                        <Link
                            to="/edi/board-brief"
                            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold)]/10 transition-colors duration-200"
                        >
                            <ClipboardCheck className="h-4 w-4" strokeWidth={2} />
                            Board Brief
                        </Link>
                        <Link
                            to="/edi/value-calculator"
                            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold)]/10 transition-colors duration-200"
                        >
                            <Sliders className="h-4 w-4" strokeWidth={2} />
                            Value Calculator
                        </Link>
                        <Link
                            to="/edi/knowledge-intelligence"
                            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold)]/10 transition-colors duration-200"
                        >
                            <Brain className="h-4 w-4" strokeWidth={2} />
                            Knowledge Intelligence
                        </Link>
                        <Link
                            to="/edi/intellectual-capital"
                            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold)]/10 transition-colors duration-200"
                        >
                            <ShieldCheck className="h-4 w-4" strokeWidth={2} />
                            Intellectual Capital
                        </Link>
                        <Link
                            to="/edi/enterprise-value"
                            data-testid={EDI.goEnterpriseValue}
                            className="inline-flex items-center gap-2 rounded-md border border-[color:var(--sgsb-gold)]/40 text-[var(--sgsb-gold)] px-4 py-2 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold)]/10 transition-colors duration-200"
                        >
                            <Coins className="h-4 w-4" strokeWidth={2} />
                            Enterprise Value
                        </Link>
                    </div>

                    <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                        {[
                            ["Modules",           `${EDI_MODULES.length}`],
                            ["Knowledge Assets",  "2.41M"],
                            ["Reuse Rate",        "47.3%"],
                            ["Hours Saved",       "218,400"],
                            ["Est. Value",        "$32.76M"],
                        ].map(([k, v]) => (
                            <div key={k} className="rounded-md bg-slate-950/40 border border-slate-800 p-3">
                                <div className="kicker text-slate-500 text-[10px]">{k}</div>
                                <div className="mt-1 font-display font-black text-white text-xl tracking-tight">{v}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Five Strategic Pillars */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                {PILLARS.map((p) => {
                    const Icon = p.icon;
                    return (
                        <div
                            key={p.key}
                            data-testid={EDI.pillar(p.key)}
                            className="glass hover-lift rounded-xl p-4 relative overflow-hidden"
                        >
                            <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                            <div className="flex items-center gap-2 text-[var(--sgsb-gold)]">
                                <Icon className="h-4 w-4" strokeWidth={1.5} />
                                <span className="kicker text-[10px]">Pillar</span>
                            </div>
                            <div className="mt-2 font-display font-black text-white text-lg tracking-tight">{p.name}</div>
                            <p className="mt-1 text-slate-400 text-xs font-body leading-relaxed">{p.desc}</p>
                        </div>
                    );
                })}
            </div>

            {/* Sections */}
            {EDI_SECTIONS.map((section) => {
                const modules = grouped[section.key] || [];
                if (!modules.length) return null;
                return (
                    <div key={section.key} className="space-y-4">
                        <div className="flex items-center gap-3">
                            <span className="h-2 w-2 rounded-full" style={{ background: section.accent }} />
                            <div className="kicker text-slate-400">{section.key}</div>
                            <div className="flex-1 h-px bg-slate-800/70" />
                            <span className="text-[11px] text-slate-500 font-body">{modules.length} capabilities</span>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {modules.map((m) => {
                                const Icon = ICONS[m.icon] || Sparkles;
                                const accent = SECTION_ACCENT[m.section] || "#D4AF37";
                                return (
                                    <Link
                                        key={m.key}
                                        to={`/edi/module/${m.key}`}
                                        data-testid={EDI.moduleCard(m.key)}
                                        className="group glass hover-lift rounded-xl p-5 relative"
                                    >
                                        <div className="absolute top-0 left-0 h-[2px] w-10" style={{ background: accent }} />
                                        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                            <ArrowUpRight className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="h-10 w-10 rounded-md border grid place-items-center"
                                                style={{ borderColor: `${accent}66`, background: `${accent}12` }}
                                            >
                                                <Icon className="h-5 w-5" style={{ color: accent }} strokeWidth={1.5} />
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-display font-black text-white text-[15px] tracking-tight leading-tight truncate">
                                                    {m.name}
                                                </div>
                                                <div className="kicker text-slate-500 text-[10px] mt-0.5">{m.section}</div>
                                            </div>
                                        </div>
                                        <p className="mt-4 text-slate-400 text-sm font-body leading-relaxed line-clamp-3">
                                            {m.tagline}
                                        </p>
                                        <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-body">
                                            {m.kpis.slice(0, 2).map((kpi) => (
                                                <div key={kpi.label} className="rounded-md bg-slate-950/40 border border-slate-800 p-2">
                                                    <div className="text-slate-500 text-[10px] uppercase tracking-wider">{kpi.label}</div>
                                                    <div className="font-display font-bold text-slate-200 mt-0.5">{kpi.value}</div>
                                                </div>
                                            ))}
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
